/**
 * I'd Eat That — Phone Pass Play state machine.
 *
 * 100% local state (no DB rows). Mirrors the LIVE game's course-major
 * flow exactly:
 *
 *   for course in appetizer → main → dessert:
 *     1. every human writes THAT course (phone passes between writers),
 *        picking which of their remaining cards it uses (≥1, like live)
 *     2. each player's course is presented one at a time and everyone
 *        else votes "I'd eat that!" / "no thanks" on it
 *   then final scores.
 *
 * That's the same state machine as the server's `active_course` loop
 * (building → presenting → building(course+1) → … → results) — the
 * old pass-play flow (write all 3 courses upfront, then present a
 * player's whole meal at once) didn't match and confused people who
 * learned the game online.
 *
 * AI seats: courses are pre-filled from the local persona templates at
 * deal time so the pass-the-phone flow never waits on them. When the
 * device is online, IdEatThatPassPlayApp fires Claude Haiku upgrades
 * that overwrite an AI course's TEXT (same cards) before it's shown —
 * see src/lib/ai/passplayAi.ts. Offline, templates just play as-is.
 */

import { INGREDIENT_CARDS } from '../data/ingredientCards'
import {
  generateSingleCourseFor, partitionHandForCourses, decideVoteFor,
} from '../../../lib/ai/personas'
import type { PassPlayPlayer } from '../../passplay/types'

// Matches the live game's deal (start_game deals 7 per player).
const HAND_SIZE = 7

export type IetPassPlayPhase =
  | 'intro'                  // explain rules, deal cards
  | 'pass_to_write'          // splash before currentPlayer writes THIS course
  | 'write_course'           // currentPlayer writes the current course
  | 'course_all_in'          // everyone's course N is in → start presenting
  | 'present_intro'          // "X is presenting course N"
  | 'pass_to_vote'           // splash before currentVoter votes
  | 'vote_course'            // currentVoter taps yes/no
  | 'results'                // final scoreboard

export interface CourseEntry {
  text: string
  ids: string[]
}

export interface PlayerMeal {
  hand: string[]                        // card ids dealt to this player
  /** Course content. Filled course-by-course as the game progresses
   *  (AI seats are pre-filled at deal time). */
  c1: CourseEntry | null
  c2: CourseEntry | null
  c3: CourseEntry | null
}

export interface CourseVoteRecord {
  /** voterId → did they say yes? */
  votes: Record<string, boolean>
}

export interface IetPassPlayState {
  players: PassPlayPlayer[]
  phase: IetPassPlayPhase
  /** Which course (1/2/3) the WHOLE table is on — writing and voting. */
  currentCourse: 1 | 2 | 3
  currentPlayerIdx: number      // writer index during write phases
  currentVoterIdx: number       // voter index during vote phases
  meals: Record<string, PlayerMeal>
  /** playerId → { c1: { voterId → bool }, c2, c3 } */
  votes: Record<string, { c1: CourseVoteRecord; c2: CourseVoteRecord; c3: CourseVoteRecord }>
  /** Who's being presented right now. Index into players array. */
  presenterIdx: number
}

function shuffle<T>(arr: T[]): T[] {
  const a = arr.slice()
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

function styleKeyForName(name: string): string {
  const lower = name.toLowerCase()
  if (lower.includes('chef'))   return 'chef'
  if (lower.includes('goblin')) return 'goblin'
  if (lower.includes('haiku'))  return 'haiku'
  if (lower.includes('critic') || lower.includes('vince')) return 'critic'
  if (lower.includes('tour'))   return 'tourist'
  if (lower.includes('kid'))    return 'kid'
  return 'chef'
}

export function courseKeyOf(course: 1 | 2 | 3): 'c1' | 'c2' | 'c3' {
  return course === 1 ? 'c1' : course === 2 ? 'c2' : 'c3'
}

/** Cards a player hasn't spent on an earlier course yet. */
export function remainingHand(meal: PlayerMeal): string[] {
  const used = new Set([
    ...(meal.c1?.ids ?? []),
    ...(meal.c2?.ids ?? []),
    ...(meal.c3?.ids ?? []),
  ])
  return meal.hand.filter(id => !used.has(id))
}

export function createState(players: PassPlayPlayer[]): IetPassPlayState {
  // Fresh nonce per match so AI template picks differ game-to-game —
  // a stable seed here meant "Greg's appetizer is the same sentence
  // shape every single game", which players clocked immediately.
  const nonce = Math.random().toString(36).slice(2, 10)
  const meals: Record<string, PlayerMeal> = {}
  for (const p of players) {
    const allIds = INGREDIENT_CARDS.map(c => c.id)
    const hand = shuffle(allIds).slice(0, HAND_SIZE)
    meals[p.id] = { hand, c1: null, c2: null, c3: null }
  }
  // Pre-fill AI meals immediately — the pass flow never waits on a bot.
  // (Online, Haiku upgrades overwrite these texts before they're shown.)
  for (const p of players) {
    if (p.kind !== 'ai') continue
    const hand = meals[p.id].hand
    const seed = `passplay:${nonce}:${p.id}`
    const [c1ids, c2ids, c3ids] = partitionHandForCourses(hand, seed)
    const sk = styleKeyForName(p.name)
    meals[p.id] = {
      hand,
      c1: generateSingleCourseFor(sk, c1ids, 1, `${seed}:1`),
      c2: generateSingleCourseFor(sk, c2ids, 2, `${seed}:2`),
      c3: generateSingleCourseFor(sk, c3ids, 3, `${seed}:3`),
    }
  }
  const votes: IetPassPlayState['votes'] = Object.fromEntries(
    players.map(p => [p.id, {
      c1: { votes: {} }, c2: { votes: {} }, c3: { votes: {} },
    }]),
  )
  return {
    players,
    phase: 'intro',
    currentCourse: 1,
    currentPlayerIdx: 0,
    currentVoterIdx: 0,
    meals,
    votes,
    presenterIdx: 0,
  }
}

/** Intro tap → first human writes course 1. */
export function startWriting(s: IetPassPlayState): IetPassPlayState {
  return advanceToWriter(s, 0)
}

/** Find the next HUMAN who hasn't written the current course yet.
 *  AI seats are pre-filled so they're skipped. When everyone's in,
 *  the course moves to the presenting loop. */
function advanceToWriter(s: IetPassPlayState, startIdx: number): IetPassPlayState {
  const key = courseKeyOf(s.currentCourse)
  let idx = startIdx
  while (idx < s.players.length) {
    const p = s.players[idx]
    if (p.kind === 'ai' || s.meals[p.id][key] !== null) { idx++; continue }
    return { ...s, phase: 'pass_to_write', currentPlayerIdx: idx }
  }
  return { ...s, phase: 'course_all_in' }
}

export function readyToWrite(s: IetPassPlayState): IetPassPlayState {
  return { ...s, phase: 'write_course' }
}

/** Current writer submits the current course (text + the cards it uses). */
export function submitCourse(s: IetPassPlayState, entry: CourseEntry): IetPassPlayState {
  const cur = s.players[s.currentPlayerIdx]
  const key = courseKeyOf(s.currentCourse)
  const next: IetPassPlayState = {
    ...s,
    meals: { ...s.meals, [cur.id]: { ...s.meals[cur.id], [key]: entry } },
  }
  return advanceToWriter(next, s.currentPlayerIdx + 1)
}

/** "Everyone's course is in" tap → present the first player's course. */
export function startPresentingCourse(s: IetPassPlayState): IetPassPlayState {
  return { ...s, phase: 'present_intro', presenterIdx: 0 }
}

export function readyForCourse(s: IetPassPlayState): IetPassPlayState {
  return advanceToVoter(s, 0)
}

function advanceToVoter(s: IetPassPlayState, startIdx: number): IetPassPlayState {
  const presenter = s.players[s.presenterIdx]
  const courseText = s.meals[presenter.id][courseKeyOf(s.currentCourse)]?.text ?? ''
  let st = s
  let idx = startIdx
  while (idx < st.players.length) {
    const v = st.players[idx]
    if (v.id === presenter.id) { idx++; continue }   // can't vote on your own
    if (v.kind === 'ai') {
      // AI auto-votes deterministically.
      const yesRate = aiYesRateFor(v.name)
      const yes = decideVoteFor(yesRate, v.id, presenter.id, st.currentCourse, courseText)
      st = recordVote(st, presenter.id, st.currentCourse, v.id, yes)
      idx++
      continue
    }
    return { ...st, phase: 'pass_to_vote', currentVoterIdx: idx }
  }
  return advancePresenterOrCourse(st)
}

function aiYesRateFor(name: string): number {
  const lower = name.toLowerCase()
  if (lower.includes('chef'))   return 0.55
  if (lower.includes('goblin')) return 0.85
  if (lower.includes('haiku'))  return 0.5
  if (lower.includes('critic') || lower.includes('vince')) return 0.3
  if (lower.includes('tour'))   return 0.7
  if (lower.includes('kid'))    return 0.75
  return 0.6
}

function recordVote(
  s: IetPassPlayState,
  presenterId: string,
  course: 1 | 2 | 3,
  voterId: string,
  yes: boolean,
): IetPassPlayState {
  const courseKey = courseKeyOf(course)
  const presenterVotes = s.votes[presenterId]
  return {
    ...s,
    votes: {
      ...s.votes,
      [presenterId]: {
        ...presenterVotes,
        [courseKey]: {
          votes: { ...presenterVotes[courseKey].votes, [voterId]: yes },
        },
      },
    },
  }
}

export function readyToVote(s: IetPassPlayState): IetPassPlayState {
  return { ...s, phase: 'vote_course' }
}

export function castVote(s: IetPassPlayState, yes: boolean): IetPassPlayState {
  const presenter = s.players[s.presenterIdx]
  const voter = s.players[s.currentVoterIdx]
  const next = recordVote(s, presenter.id, s.currentCourse, voter.id, yes)
  return advanceToVoter(next, s.currentVoterIdx + 1)
}

/**
 * After the last vote on a presenter's course: next presenter, same
 * course. After the last presenter: everyone writes the NEXT course
 * (course-major — this is the order the live game uses). After
 * dessert's last presenter: results.
 */
function advancePresenterOrCourse(s: IetPassPlayState): IetPassPlayState {
  if (s.presenterIdx + 1 < s.players.length) {
    return { ...s, phase: 'present_intro', presenterIdx: s.presenterIdx + 1 }
  }
  if (s.currentCourse < 3) {
    const next: IetPassPlayState = {
      ...s,
      currentCourse: (s.currentCourse + 1) as 1 | 2 | 3,
      presenterIdx: 0,
    }
    return advanceToWriter(next, 0)
  }
  return { ...s, phase: 'results' }
}

/** Total YES votes across all 3 courses for each player. */
export function totalScores(s: IetPassPlayState): Record<string, number> {
  const out: Record<string, number> = {}
  for (const p of s.players) {
    const v = s.votes[p.id]
    let sum = 0
    for (const k of ['c1', 'c2', 'c3'] as const) {
      sum += Object.values(v[k].votes).filter(Boolean).length
    }
    out[p.id] = sum
  }
  return out
}
